import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

import { PlautoService, PlpropertyService } from '@amfam/policy/data-access';
import {
  AutoPolicy,
  Coverage,
  GenericProductType,
  Policy,
  PropertyPolicy,
  SourceSystem,
  SourceSystemType
} from '@amfam/policy/models';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { CopyService, Policy101ClicktipModel } from '@amfam/shared/utility/shared-services';

import { CoveragesModel } from './coverages.model';

@Component({
  selector: 'app-coverages',
  templateUrl: './coverages.component.html',
  styleUrls: ['./coverages.component.scss'],
  standalone: false
})
export class CoveragesComponent implements OnChanges {
  public coverageModels = new Array<CoveragesModel>();
  public additionalCoverages = new Array<Coverage>();
  public isPrimary = false;
  public isSecondary = false;

  @Input() policy: Policy;
  @Input() newAFITemplate: boolean = false;
  @Input() vehicleId?: string;

  @Output() show: EventEmitter<boolean> = new EventEmitter<boolean>();

  format: boolean;
  public hideTermDescription = false;
  public hideTermDescriptionForRome = !!this.featureFlag.isEnabled(
    'hide_term_description_for_rome_property'
  );

  constructor(
    private autoPolicyService: PlautoService,
    private propertyPolicyService: PlpropertyService,
    private copyService: CopyService,
    private featureFlag: FeatureFlagService
  ) {}

  ngOnChanges() {
    let policyCoverages: Coverage[] = [];
    let vehicleCoverages: Coverage[] = [];
    this.coverageModels = [];
    this.additionalCoverages = [];
    this.format = this.policy?.sourcePath !== SourceSystem.ROME;

    /* There is some special processing that needs to happen.
    If the policy is auto, it needs to be processed so that we are looking at all the coverages for all the vehicles.
    Coverages existing for ALL vehicles are POLICY level coverages, others are RISK level coverages.
    This does not apply to other policy types, in that case, all coverages are POLICY level coverages.
    */
    switch (this.policy.generalizedProductType) {
      case GenericProductType.Auto:
        policyCoverages = this.autoPolicyService.getPolicyLevelCoverages(
          <AutoPolicy>this.policy,
          this.vehicleId
        );
        break;

      case GenericProductType.Home:
        policyCoverages = this.propertyPolicyService.getPolicyLevelCoverages(
          <PropertyPolicy>this.policy
        );
        this.hideTermDescription =
          this.policy?.sourcePath === SourceSystem.ROME && this.hideTermDescriptionForRome;
        break;

      default:
        for (const policyCoverage of this.policy.policyCoverages) {
          policyCoverages.push(policyCoverage);
        }
        break;
    }

    // Seperate coverages into actual coverages and additional coverages
    for (const coverage of policyCoverages) {
      // Coverage object is adjusted to handle the rejected coverages.
      const adjustedCoverage = this.handleRejectedCoverage(
        coverage,
        'PAUIMCov_af',
        'IN',
        'REJECTED:'
      );
      if (adjustedCoverage) {
        if (
          (adjustedCoverage.limitTerms.length > 0 && adjustedCoverage.limitTerms[0].value !== '') ||
          (adjustedCoverage.deductibleTerms.length > 0 &&
            adjustedCoverage.deductibleTerms[0].value !== '')
        ) {
          const coverageModel = new CoveragesModel(
            adjustedCoverage,
            this.getClicktipContent(adjustedCoverage.code)
          );
          if (coverageModel.isVisible) {
            this.coverageModels.push(coverageModel);
          }
        } else if (
          adjustedCoverage.otherTerms &&
          adjustedCoverage.otherTerms[0]?.valueCode !== 'Rejected' &&
          !adjustedCoverage.rejectedCoverage
        ) {
          // Only display additional coverages if customer has not Rejected them
          this.additionalCoverages.push(adjustedCoverage);
        }
      }
    }

    for (const role of this.policy.policyRoles) {
      if (role.roleType.toLowerCase() === 'primarynamedinsured') {
        this.isPrimary = true;
      } else if (role.roleType.toLowerCase() === 'secondarynamedinsured') {
        this.isSecondary = true;
      }
      if (this.isPrimary && this.isSecondary) {
        break;
      }
    }
    if (this.newAFITemplate && this.vehicleId) {
      const vehicle = this.policy.vehicles.find(v => v.vehicleId === this.vehicleId);
      vehicleCoverages = this.autoPolicyService.getRiskLevelCoverages(
        <AutoPolicy>this.policy,
        vehicle,
        true
      );
      this.coverageModels = [
        ...vehicleCoverages.map(vc => new CoveragesModel(vc, this.getClicktipContent(vc.code))),
        ...this.coverageModels
      ];
      this.coverageModels = this.sortCoverages(this.coverageModels);
      this.additionalCoverages.forEach(ac => this.coverageModels.push(new CoveragesModel(ac, '')));
      this.additionalCoverages = [];
    }
    this.show.emit(this.coverageModels.length > 0 || this.additionalCoverages.length > 0);
  }

  private sortCoverages(arr) {
    const n = arr.length;
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n - i - 1; j++) {
        if (arr[j].coverage.name > arr[j + 1].coverage.name) {
          // Swap elements
          [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
        }
      }
    }
    return arr;
  }

  private handleRejectedCoverage(
    coverage: Coverage,
    coverageCode: string,
    state: string,
    searchString: string,
    replaceString?: string
  ) {
    if (
      this.policy &&
      this.policy.sourceSystem === SourceSystemType.Advance &&
      this.policy.policyAddress.state === state &&
      coverage.code === coverageCode &&
      !!coverage.rejectedCoverage
    ) {
      return {
        ...coverage,
        showRejected: true,
        name: coverage.name.replace(searchString, replaceString || ''),
        description: coverage.description.replace(searchString, replaceString || '')
      };
    }
    // Hide rejected coverages for all other states
    return !coverage.rejectedCoverage ? coverage : null;
  }

  private getClicktipContent(coverageCode: string) {
    return this.copyService
      .getCopy('policy.policy101')
      .find((clicktipContent: Policy101ClicktipModel) => {
        return clicktipContent.codes.includes(coverageCode);
      });
  }
}
